import { SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Modal } from 'antd'
import arrayMove from 'array-move'
import AccordionItem from 'components/Elements/StyledAccordionItem'
import StyledLoader from 'components/Elements/StyledLoader'
import { DataContext } from 'contexts/DataContextContainer'
import moment from 'moment'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Accordion, Carousel } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import { useLocation } from 'react-router'
import axiosClient from 'services/axios-client'
import { colors } from 'style'
import Answers from './Answer'
import Question from './Question'
import { StoryContainer } from './style'

import { DndContext } from '@dnd-kit/core'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import SectionTitle from 'components/SectionTitle'
import AnswerPreviewModal from 'sections/Dashboard/components/AnswerPreviewModal'
import PromptRolesModal from 'sections/Dashboard/components/PromptRolesModal'
import { useAtom } from 'jotai'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { useParams } from 'react-router-dom'
import { TUT_IDS } from 'services/constants'
import {
  activeAnswerAtom,
  activeAnswerImagesAtom,
  activeQuestionAtom,
  showAnswerEditModalAtom,
  showAnswerPreviewModalAtom,
} from 'store/atoms'
import {
  useBookQuery,
  useBookQuestionsQuery,
  useBookRoleQuery,
  usePromptOrderMutation,
} from 'store/queries'
import AnswerModal from './Answer/AnswerModal'

function Story() {
  const { t } = useTranslation()
  const location = useLocation()
  const { promptRolesModalConfig, setPromptRolesModalConfig } =
    useContext(DataContext)
  const queryClient = useQueryClient()

  const [, setActiveAnswer] = useAtom(activeAnswerAtom)
  const [activeQuestion, setActiveQuestion] = useAtom(activeQuestionAtom)
  const [activeAnswerImages, setActiveAnswerImages] = useAtom(
    activeAnswerImagesAtom,
  )

  // TODO: Make these regular state.
  const [showAnswerEditModal, setShowAnswerEditModal] = useAtom(
    showAnswerEditModalAtom,
  )
  const { bookId, filter } = useParams()

  const { data: book } = useBookQuery(bookId)
  const { data: role } = useBookRoleQuery(bookId)

  const { isRefetching: isFetchingQuestions, data: questions } =
    useBookQuestionsQuery(bookId)

  const orderMutation = usePromptOrderMutation(bookId)
  const [showModalImages, setShowModalImages] = useState(false)
  const [selectedToggle, setSelectedToggle] = useState('')
  const [localQuestions, setLocalQuestions] = useState([])
  const [showAnswerPreviewModal, setShowAnswerPreviewModal] = useAtom(
    showAnswerPreviewModalAtom,
  )

  useEffect(() => {
    setLocalQuestions(questions)
  }, [questions])

  const handleEditAnswer = useCallback(
    (question, answer) => {
      setActiveQuestion(question)
      setActiveAnswer(answer)
      setShowAnswerEditModal(true)
    },
    [setActiveAnswer, setActiveQuestion, setShowAnswerEditModal],
  )

  const handleViewAnswer = useCallback(
    (question, answer) => {
      setActiveQuestion(question)
      setActiveAnswer(answer)
      setShowAnswerPreviewModal(true)
    },
    [setActiveAnswer, setActiveQuestion],
  )

  useEffect(() => {
    // Handle direct link to a prompt.
    const params = new URLSearchParams(location.search)

    const directLinkQuestionID = params.get('question')
    const directLinkAnswerID = params.get('answer')
    const isEdit = params.get('is_edit')
    const shouldLaunchRoleEdit = params.get('edit_roles')

    if (questions?.length && directLinkQuestionID && directLinkAnswerID) {
      const linkedQuestion = questions.find(
        (q) => q.id.toString() === directLinkQuestionID,
      )

      if (linkedQuestion) {
        const linkedAnswer = linkedQuestion.answers.find(
          (a) => a.id.toString() === directLinkAnswerID,
        )

        if (isEdit) {
          handleEditAnswer(linkedQuestion, linkedAnswer)
        } else {
          handleViewAnswer(linkedQuestion, linkedAnswer)
        }

        const newParams = new URLSearchParams(location.search)
        newParams.delete('question')
        newParams.delete('answer')
        newParams.delete('is_edit')

        location.search = newParams.toString()
      }
    } else if (
      questions?.length &&
      directLinkQuestionID &&
      shouldLaunchRoleEdit
    ) {
      const linkedQuestion = questions.find(
        (q) => q.id.toString() === directLinkQuestionID,
      )

      if (linkedQuestion) {
        setPromptRolesModalConfig({
          type: 2,
          question: linkedQuestion,
          show: true,
        })

        const newParams = new URLSearchParams(location.search)
        newParams.delete('question')
        newParams.delete('edit_roles')

        location.search = newParams.toString()
      }
    }
  }, [
    location,
    questions,
    handleEditAnswer,
    handleViewAnswer,
    setPromptRolesModalConfig,
  ])

  const onCloseModalImages = () => {
    setShowModalImages(false)
  }

  const addNewStory = (e, question) => {
    e.stopPropagation()

    // Right now we automatically create blank answers for authors, so we need to check
    const existingAnswer = questions
      .find((q) => q.id === question.id)
      .answers.find((a) => a.user_id === role?.id)

    if (existingAnswer) {
      setActiveAnswer(existingAnswer)
    } else {
      setActiveAnswer(null)
    }

    setActiveQuestion(question)
    setActiveAnswerImages([])

    setShowAnswerEditModal(true)
  }

  const onViewImages = async (e, answer) => {
    e.stopPropagation()
    const imagesResult = await axiosClient.get(
      `/book/${bookId}/question/${answer.question_id}/answer/${answer.id}/images`,
    )
    if (imagesResult.ok) {
      setActiveAnswerImages(imagesResult.data)
      setShowModalImages(true)
    }
  }

  const filteredPrompts = useMemo(() => {
    let processedPrompts = localQuestions
    if (role?.name === 'Guest' || role?.name === 'None') {
      processedPrompts = questions
        .map((q) => ({
          ...q,
          answers: q.answers.filter((a) => {
            return a.user_id === role?.id
          }),
        }))
        .filter((q) => {
          return q.is_sent && q.answers.length
        })
    }

    processedPrompts = processedPrompts.map((q) => ({
      ...q,
      allSubmitted: q.answers.length && !q.answers.some((a) => !a.is_submitted),
    }))

    switch (filter) {
      case 'sent':
        processedPrompts = processedPrompts.filter((q) => q.is_sent)
        break
      case 'pending':
        // TODO: Could have some hooks like `usePendingPrompts`.
        processedPrompts = processedPrompts.filter((q) => !q.is_sent)
        break
      case 'shared':
        processedPrompts = processedPrompts.filter((q) => {
          return q.is_sent && q.answers.some((a) => a.is_submitted)
        })
        processedPrompts.forEach((q) => {
          q.answers = q.answers.filter((a) => a.is_submitted)
        })
        break
      case 'not-shared':
        processedPrompts = processedPrompts.filter((q) => {
          return (
            q.is_sent &&
            (q.answers.some((a) => !a.is_submitted) || !q.answers.length)
          )
        })
        processedPrompts.forEach((q) => {
          q.answers = q.answers.filter((a) => !a.is_submitted)
        })
        break
      case 'mine':
        processedPrompts = processedPrompts.map((q) => {
          return {
            ...q,
            answers: q.answers.filter((a) => {
              return a.user_id === role?.id
            }),
          }
        })
        break
      default:
        // Show all prompts.
        break
    }
    return processedPrompts
  }, [localQuestions, questions, role?.name, filter, role?.id])

  const sentPrompts = []
  const unsentPrompts = []

  filteredPrompts?.forEach((q) => {
    if (q.is_sent) {
      sentPrompts.push(q)
    } else {
      unsentPrompts.push(q)
    }
  })

  const pendingPrompts = []
  const afterCutoffPrompts = []
  const cutOffDate = moment(book?.end_datetime).format('YYYY-MM-DD')

  if (filter === 'pending' || filter === 'all') {
    unsentPrompts.forEach((q) => {
      const willSendTime = moment(q.will_send_on_datetime).format('YYYY-MM-DD')

      if (willSendTime < cutOffDate) {
        pendingPrompts.push(q)
      } else if (willSendTime >= cutOffDate) {
        afterCutoffPrompts.push(q)
      }
    })
  }

  const onSortQuestions = (e) => {
    const { active, over } = e

    if (active?.id !== over?.id) {
      const oldIndex = localQuestions.findIndex((p) => p.id === active.id)
      const newIndex = localQuestions.findIndex((p) => p.id === over.id)
      const newPrompts = arrayMove(localQuestions, oldIndex, newIndex)

      newPrompts.forEach((p, i) => {
        p.sort = i
      })

      // TODO: Do this with mutation only and no state variable.
      setLocalQuestions(newPrompts)

      orderMutation.mutate({
        prompts: newPrompts,
        isConfirmed: book.story_order_confirmed,
      })
    }
  }

  const SortablePrompt = ({ question = {}, i, isAfterCutoff, className }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: question.id })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    }
    return (
      <div
        key={i}
        ref={setNodeRef}
        className={className}
        style={style}
        {...attributes}
      >
        <AccordionItem
          eventKey={`question-item-${i}-pending${
            isAfterCutoff ? '-post-cutoff' : ''
          }`}
          className={`${i % 2 === 0 ? 'bg-white' : 'bg-white'}`}
          selectedToggle={selectedToggle}
          setSelectedToggle={setSelectedToggle}
          renderToggle={() => (
            <Question
              question={question}
              bookDetail={book}
              addNewStory={addNewStory}
              isAfterCutoff={isAfterCutoff}
              dndListeners={listeners}
            />
          )}
          renderWhenExpanded={() => (
            <Answers
              answers={question.answers}
              question={question}
              bookDetail={book}
              onEditAnswer={handleEditAnswer}
              onViewImages={onViewImages}
              onViewAnswer={handleViewAnswer}
              selectedToggle={selectedToggle}
              setSelectedToggle={setSelectedToggle}
            />
          )}
        />
      </div>
    )
  }

  if (isFetchingQuestions) {
    return <StyledLoader />
  }

  return (
    <StoryContainer
      id="story-section"
      data-tut={TUT_IDS.storySection}
      className="section-container"
    >
      <div className="stories-wrapper">
        {/* TODO: Make this not an accordion */}
        <Accordion
          activeKey="0"
          className="accordion-story"
          onClick={(e) => e.preventDefault()}
        >
          <Accordion.Toggle
            variant="link"
            as={'div'}
            className="accordion-toggler px-3 py-2 story-section bb-line"
            eventKey="0"
          >
            <div className="toggler-header">
              <SectionTitle>{t('stories')}</SectionTitle>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            {/* TODO: Simplify. */}
            {!localQuestions?.length ? (
              <p className="mt-3">Add some story prompts to get started.</p>
            ) : filter === 'pending' && pendingPrompts.length === 0 ? (
              <p>All prompts have been sent.</p>
            ) : (
              <DndContext onDragEnd={onSortQuestions}>
                {/* !! Neccessary as bools are encoded as numbers and SortableContext sensibly expects an actual bool */}
                <SortableContext
                  items={filteredPrompts.map((q) => q.id)}
                  disabled={!!book.is_published}
                >
                  {!!sentPrompts.length && (
                    <hr
                      className="hr-text sent-line"
                      data-content="Sent Prompts"
                    />
                  )}
                  <Accordion activeKey={selectedToggle}>
                    {sentPrompts?.map((p, i) => (
                      <AccordionItem
                        key={p.id}
                        eventKey={`question-item${i}`}
                        className={classNames(
                          i % 2 === 0 ? 'bg-white' : 'bg-white',
                          i === 0 ? 'first-story' : '',
                        )}
                        selectedToggle={selectedToggle}
                        setSelectedToggle={setSelectedToggle}
                        renderToggle={() => (
                          <Question
                            question={p}
                            bookDetail={book}
                            addNewStory={addNewStory}
                            isAfterCutoff={false}
                          />
                        )}
                        renderWhenExpanded={() => (
                          <Answers
                            answers={p.answers}
                            question={p}
                            bookDetail={book}
                            onEditAnswer={handleEditAnswer}
                            onViewImages={onViewImages}
                            onViewAnswer={handleViewAnswer}
                            selectedToggle={selectedToggle}
                            setSelectedToggle={setSelectedToggle}
                          />
                        )}
                      />
                    ))}
                  </Accordion>

                  {!!pendingPrompts.length && (
                    <hr
                      className="hr-text pending-line"
                      data-content={t('pending_prompts')}
                    />
                  )}
                  <Accordion
                    activeKey={selectedToggle}
                    data-testid="pending-prompts-section"
                  >
                    {pendingPrompts.map((p, i) => (
                      <SortablePrompt
                        className="prompt-row"
                        key={p.id}
                        question={p}
                        i={i}
                        isAfterCutoff={false}
                      />
                    ))}
                  </Accordion>

                  {!!afterCutoffPrompts.length && (
                    <hr
                      className="hr-text cutoff-line"
                      data-content={t('project_cut_off')}
                    />
                  )}
                  <Accordion activeKey={selectedToggle}>
                    {afterCutoffPrompts.map((p, i) => (
                      <SortablePrompt
                        key={p.id}
                        question={p}
                        i={i}
                        isAfterCutoff={true}
                      />
                    ))}
                  </Accordion>
                </SortableContext>
              </DndContext>
            )}
          </Accordion.Collapse>
        </Accordion>
      </div>

      <div id="story-section-bottom"></div>

      {promptRolesModalConfig.show && (
        <PromptRolesModal
          config={promptRolesModalConfig}
          onUpdate={() => {
            queryClient.invalidateQueries({
              queryKey: ['book', bookId, 'questions'],
            })
            queryClient.invalidateQueries({
              queryKey: ['book', bookId, 'users'],
            })
          }}
        />
      )}

      {showAnswerEditModal && (
        <AnswerModal
          bookDetail={book}
          show={showAnswerEditModal}
          onHide={() => setShowAnswerEditModal(false)}
          onAnswerChange={(answer) => handleEditAnswer(activeQuestion, answer)}
        />
      )}

      {showAnswerPreviewModal && <AnswerPreviewModal bookId={bookId} />}

      <Modal
        title={t('pictures')}
        visible={showModalImages}
        onCancel={onCloseModalImages}
        maskClosable={false}
        footer={false}
        wrapClassName="modal-images"
        getContainer={() => document.getElementById('story-section')}
      >
        <Carousel
          nextIcon={<FaArrowRight color={colors.dark8} />}
          prevIcon={<FaArrowLeft color={colors.dark8} />}
          interval={60000}
        >
          {activeAnswerImages.map((_image, _i) => (
            <Carousel.Item key={_i}>
              <img className="d-block" src={_image.url} alt="First slide" />
              <Carousel.Caption>
                <p>{_image.caption}</p>
              </Carousel.Caption>
            </Carousel.Item>
          ))}
        </Carousel>
      </Modal>
    </StoryContainer>
  )
}

export default Story
